import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Sparkles } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { DevelopmentPriorities, type Assessment } from "./KidAssessmentTab";
import { getCheckpointLabel } from "@/config/assessmentCheckpoints";
import type { KidProfile, SessionSummary } from "./types";

interface Props {
  kid: KidProfile;
}

// ─── Trend (overall composite, first-half vs second-half of attended sessions) ──

function avg(vals: (number | null)[]): number | null {
  const nums = vals.filter((v): v is number => v != null);
  return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : null;
}

function sessionComposite(s: SessionSummary): number | null {
  if (!s.attended || !s.skills) return null;
  const sk = s.skills;
  return avg([
    sk.battingGrip, sk.battingStance, sk.battingFootwork, sk.battingTransfer, sk.battingHead,
    sk.bowlingLoad, sk.bowlingBfc, sk.bowlingBrace, sk.bowlingRelease,
    sk.fieldingCatch, sk.fieldingThrow, sk.fieldingGround,
  ]);
}

function computeTrend(sessions: SessionSummary[]): "Improving" | "Steady" | "Needs work" {
  // sessions are newest-first — reverse for chronological order
  const chronological = [...sessions].reverse();
  const vals = chronological.map(sessionComposite).filter((v): v is number => v != null);
  if (vals.length < 4) return "Steady";
  const half = Math.floor(vals.length / 2);
  const first = avg(vals.slice(0, half)) ?? 0;
  const last = avg(vals.slice(-half)) ?? 0;
  const delta = last - first;
  if (delta >= 0.3) return "Improving";
  if (delta <= -0.3) return "Needs work";
  return "Steady";
}

// ─── AI summary stub (client-side template — PR-AI-1 replaces with a real call) ──

function generateProgressSummary(
  kid: KidProfile,
  topPriorityLabel: string | null,
  trend: string,
): string {
  const lastSession = kid.sessions?.[0];
  if (!lastSession) return `${kid.firstName} hasn't had any sessions recorded yet.`;

  const attended = lastSession.attended;
  const rating = lastSession.skillRating ?? 0;

  const line1 = attended
    ? `${kid.firstName} attended the last session (${lastSession.theme}) and rated ${rating}/3 overall.`
    : `${kid.firstName} missed the last session on ${lastSession.date}.`;

  const line2 = topPriorityLabel
    ? `Coach's current development focus: ${topPriorityLabel}.`
    : `Overall trend is ${trend} across batting, bowling, and fielding.`;

  return `${line1} ${line2}`;
}

// ─── Skill snapshot bars ─────────────────────────────────────────────────────

function SkillBar({ label, value, color }: { label: string; value: number | null; color: string }) {
  const pct = value != null ? Math.min(100, (value / 5) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-gray-600">{label}</span>
        <span className="text-xs font-semibold text-gray-800">
          {value != null ? value.toFixed(1) : "—"} / 5
        </span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ─── Last 5 sessions mini-table ──────────────────────────────────────────────

function RecentSessionsTable({ sessions }: { sessions: SessionSummary[] }) {
  const recent = sessions.slice(0, 5);
  if (recent.length === 0) {
    return <p className="text-sm text-gray-400 italic">No sessions recorded yet.</p>;
  }
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-gray-100 text-left text-xs text-gray-400">
          <th className="font-medium pb-2">Date</th>
          <th className="font-medium pb-2">Theme</th>
          <th className="font-medium pb-2 text-center">Present</th>
          <th className="font-medium pb-2 text-right">Rating</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-50">
        {recent.map((s) => (
          <tr key={s.id}>
            <td className="py-2 text-gray-700">{s.date}</td>
            <td className="py-2 text-gray-700 truncate max-w-[140px]">{s.theme}</td>
            <td className="py-2 text-center">{s.attended ? "✓" : "—"}</td>
            <td className="py-2 text-right text-gray-700">{s.skillRating != null ? `${s.skillRating}/3` : "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── Main tab ───────────────────────────────────────────────────────────────

export function KidProgressTab({ kid }: Props) {
  const { data, isLoading } = useQuery<{ assessments: Assessment[] }>({
    queryKey: ["player-assessments", kid.id],
    queryFn: async () => {
      const res: any = await apiRequest("GET", `/api/players/${kid.id}/assessments`);
      return res.data;
    },
    enabled: !!kid.id,
    retry: false,
  });

  const assessments = data?.assessments ?? [];
  const mostRecent = assessments[0];
  const trend = computeTrend(kid.sessions ?? []);

  const topPriorityLabel = mostRecent
    ? (() => {
        const lowest = [...mostRecent.scores].sort((a, b) => a.score - b.score)[0];
        return lowest ? getCheckpointLabel(mostRecent.discipline, mostRecent.skillLevel, lowest.checkpointKey) : null;
      })()
    : null;

  const summary = generateProgressSummary(kid, topPriorityLabel, trend);

  return (
    <div className="space-y-5 py-2" data-testid="progress-tab-loaded">
      {/* AI Summary card */}
      <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-5" data-testid="ai-summary-card">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-4 w-4 text-indigo-500" />
          <h2 className="text-sm font-semibold text-indigo-900">Coach Update</h2>
          <Badge className="text-xs bg-indigo-100 text-indigo-700 border-indigo-200 border">
            AI-generated summary · Phase 1
          </Badge>
        </div>
        <p className="text-sm text-indigo-900 leading-relaxed">{summary}</p>
      </div>

      {/* Skill snapshot */}
      <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-700 mb-4">Skill Snapshot</h2>
        <div className="space-y-3">
          <SkillBar label="Batting" value={kid.avgBattingScore} color="bg-blue-500" />
          <SkillBar label="Bowling" value={kid.avgBowlingScore} color="bg-indigo-500" />
          <SkillBar label="Fielding" value={kid.avgFieldingScore} color="bg-sky-500" />
        </div>
      </div>

      {/* Last 5 sessions */}
      <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Recent Sessions</h2>
        <RecentSessionsTable sessions={kid.sessions ?? []} />
      </div>

      {/* Development priorities — only if a latest assessment exists */}
      {isLoading ? (
        <Skeleton className="h-32 w-full rounded-xl" />
      ) : mostRecent ? (
        <DevelopmentPriorities assessment={mostRecent} />
      ) : null}
    </div>
  );
}
